/**
 * Dual Range Slider Renderer
 *
 * Dual-handle range slider or single range slider with configurable behavior
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Dual Range Slider Renderer
 * Features:
 * - Dual handle range selection
 * - Single range mode (left handle fixed at min)
 * - Min/max/step constraints
 * - Default value support
 * - Visual range display
 */
export const renderDualRangeSlider: FieldRenderer = ({ field, value, handleChange, isRequired }) => {
    const {
        minValue = 0,
        maxValue = 100,
        step = 1,
        defaultValue,
        singleRange = false, // If true, left side is always at minValue
    } = field;

    // Initialize slider value
    const getInitialValue = () => {
        if (value !== undefined && value !== null) {
            // If singleRange, value should be a number, convert to array [minValue, number]
            if (singleRange) {
                return [minValue as number, typeof value === 'number' ? value : (maxValue as number) / 2];
            }
            // For dual range, value should be an array
            return Array.isArray(value) ? value : [minValue as number, (maxValue as number) / 2];
        }

        // Use defaultValue if provided
        if (defaultValue !== undefined) {
            if (singleRange) {
                return [minValue as number, defaultValue as number];
            }
            return Array.isArray(defaultValue) ? defaultValue : [defaultValue as number, (maxValue as number) / 2];
        }

        // Default values
        return singleRange
            ? [minValue as number, (maxValue as number) / 2]
            : [(minValue as number) + ((maxValue as number) - (minValue as number)) * 0.25, (minValue as number) + ((maxValue as number) - (minValue as number)) * 0.75];
    };

    const sliderValue = getInitialValue();

    const handleSliderChange = (newValue: number[]) => {
        if (singleRange) {
            // For single range, keep first value at minValue, only update second value
            // Only store the second value (the actual value)
            handleChange(field.name, newValue[1]);
        } else {
            // For dual range, store the entire array
            handleChange(field.name, newValue);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <Label className="leading-6">
                    {field.label}
                    {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <output className="text-sm font-medium tabular-nums">
                    {singleRange ? sliderValue[1] : `${sliderValue[0]} - ${sliderValue[1]}`}
                </output>
            </div>
            {field.description && (
                <p className="text-sm text-muted-foreground">{field.description}</p>
            )}
            <div className={singleRange ? '[&_[data-slot="slider-thumb"]:first-of-type]:hidden' : ''}>
                <Slider
                    value={sliderValue}
                    onValueChange={handleSliderChange}
                    min={minValue as number}
                    max={maxValue as number}
                    step={step}
                    aria-label={field.label}
                    minStepsBetweenThumbs={singleRange ? 0 : undefined}
                />
            </div>
        </div>
    );
};

export default renderDualRangeSlider;
